import { forwardRef, useCallback, useId, useRef, useState } from 'react'
import type React from 'react'

import { useKeyboardProvider } from '@/components/Keyboard/KeyboardProvider'
import { useCaretPosition } from '@/lib/useCaretPosition'

import { InputCaret, InputControl, InputControlWrapper, InputError, InputLabel, InputWrapper } from './Input.styles'

type InputType = 'text' | 'numeric' | 'numericAndText'

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label: string
  type?: InputType
  error?: string
  keyboard?: boolean
  onKeyboardEnter?: () => void
  onChange?: (value: string) => void
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, type = 'text', error, value, keyboard = false, onKeyboardEnter, onChange, onFocus, onBlur, maxLength, readOnly, ...props },
    ref
  ) => {
    const inputId = useId()
    const keyboardProvider = useKeyboardProvider()
    const openKeyboard = keyboardProvider?.openKeyboard
    const scheduleCloseKeyboard = keyboardProvider?.scheduleCloseKeyboard
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)
    const stringValue = String(value ?? '')
    const caretLeft = useCaretPosition(inputRef, stringValue, isFocused)

    const setRefs = (element: HTMLInputElement | null) => {
      inputRef.current = element

      if (typeof ref === 'function') {
        ref(element)
        return
      }

      if (ref) {
        ref.current = element
      }
    }

    const normalizeValue = useCallback((nextValue: string) => {
      const normalizedValue = type === 'numeric' ? nextValue.replace(/\D/g, '') : nextValue
      return typeof maxLength === 'number' ? normalizedValue.slice(0, maxLength) : normalizedValue
    }, [maxLength, type])

    const handleValueChange = useCallback((nextValue: string) => {
      onChange?.(normalizeValue(nextValue))
    }, [normalizeValue, onChange])

    const openInputKeyboard = useCallback(() => {
      if (!keyboard || !openKeyboard) {
        return
      }

      openKeyboard({
        id: inputId,
        type: type === 'numeric' ? 'numeric' : type === 'numericAndText' ? 'numericAndText' : 'text',
        value: String(value ?? ''),
        maxLength: typeof maxLength === 'number' ? maxLength : undefined,
        onChange: handleValueChange,
        onEnter: onKeyboardEnter ?? (() => inputRef.current?.blur())
      })
    }, [handleValueChange, inputId, keyboard, maxLength, onKeyboardEnter, openKeyboard, type, value])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      handleValueChange(event.target.value)
    }

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      openInputKeyboard()
      setIsFocused(true)
      onFocus?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      scheduleCloseKeyboard?.(inputId)
      setIsFocused(false)
      onBlur?.(event)
    }

    return (
      <InputWrapper>
        <InputLabel>{label}</InputLabel>
        <InputControlWrapper>
          <InputControl
            ref={setRefs}
            type="text"
            inputMode={type === 'numeric' || type === 'numericAndText' ? 'numeric' : 'text'}
            autoComplete="off"
            $hasError={Boolean(error)}
            value={value ?? ''}
            readOnly={keyboard || readOnly}
            maxLength={maxLength}
            data-keyboard-input-id={keyboard ? inputId : undefined}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />
          {isFocused && <InputCaret $left={caretLeft} />}
        </InputControlWrapper>
        <InputError>{error}</InputError>
      </InputWrapper>
    )
  }
)

Input.displayName = 'Input'

export default Input
